import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Search, Mail, Phone, Ban, CheckCircle, Users, UserCheck, UserX, UserPlus, MapPin, Grid, List, Lock, RefreshCw, Copy, Check } from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Table from '../../components/Table';
import RowActions from '../../components/RowActions';
import Modal from '../../components/Modal';
import { apiService } from '../../services/apiService';
import { formatDate, renderCell, renderCellDate, generatePassword } from '../../utils/helpers';
import { useToast } from '../../contexts/ToastContext';

const ClientsManagement = () => {
  const navigate = useNavigate();
  const { success: showSuccess, error: showError } = useToast();
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState({ open: false, client: null });
  const [passwordModal, setPasswordModal] = useState({
    open: false,
    client: null,
    currentPassword: '',
    newPassword: '',
    saving: false,
    copied: false,
  });
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'card' - default to table

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    filterClients();
  }, [searchQuery, clients]);

  const loadClients = async () => {
    try {
      setLoading(true);
      const data = await apiService.getClients();
      setClients(data);
    } catch (error) {
      console.error('Failed to load clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterClients = () => {
    if (!searchQuery) {
      setFilteredClients(clients);
      return;
    }
    const query = searchQuery.toLowerCase();
    const filtered = clients.filter(
      (client) =>
        client.name?.toLowerCase().includes(query) ||
        client.company?.toLowerCase().includes(query) ||
        client.poste?.toLowerCase().includes(query) ||
        client.email?.toLowerCase().includes(query) ||
        client.phone?.includes(query) ||
        client.city?.toLowerCase().includes(query)
    );
    setFilteredClients(filtered);
  };

  const handleDelete = async () => {
    try {
      await apiService.deleteClient(deleteModal.client.id);
      setClients(clients.filter((c) => c.id !== deleteModal.client.id));
      setDeleteModal({ open: false, client: null });
    } catch (error) {
      console.error('Failed to delete client:', error);
    }
  };

  const handleDeactivate = async (clientId) => {
    try {
      await apiService.deactivateClient(clientId);
      // Reload clients to refresh status
      await loadClients();
    } catch (error) {
      console.error('Failed to deactivate client:', error);
    }
  };

  const handleActivate = async (clientId) => {
    try {
      await apiService.activateClient(clientId);
      // Reload clients to refresh status
      await loadClients();
    } catch (error) {
      console.error('Failed to activate client:', error);
    }
  };

  const openPasswordModal = async (client) => {
    try {
      const data = await apiService.getClient(client.id);
      setPasswordModal({
        open: true,
        client,
        currentPassword: data.plainPassword || data.plain_password || '',
        newPassword: '',
        saving: false,
        copied: false,
      });
    } catch (error) {
      showError('Impossible de charger le mot de passe du client');
    }
  };

  const handlePasswordReset = async () => {
    const password = passwordModal.newPassword?.trim();
    if (!password) {
      showError('Générez ou saisissez un nouveau mot de passe');
      return;
    }

    try {
      setPasswordModal((prev) => ({ ...prev, saving: true }));
      await apiService.resetClientPassword(passwordModal.client.id, password);
      setPasswordModal((prev) => ({
        ...prev,
        currentPassword: password,
        newPassword: '',
        saving: false,
      }));
      showSuccess('Mot de passe enregistré dans la base de données');
    } catch (error) {
      setPasswordModal((prev) => ({ ...prev, saving: false }));
      showError(error.message || 'Erreur lors de la mise à jour du mot de passe');
    }
  };

  const columns = [
    {
      header: 'Code',
      accessor: 'poste',
      render: (row) => (
        <span className="text-sm font-mono font-medium text-gray-800">{renderCell(row.poste)}</span>
      ),
    },
    {
      header: 'Nom',
      accessor: 'name',
      render: (row) => (
        <div>
          <div className="flex items-center space-x-2">
            <p className="font-medium text-gray-900">{row.name}</p>
            {row.status === 'inactive' && (
              <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                Inactif
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500">{renderCell(row.company)}</p>
        </div>
      ),
    },
    {
      header: 'Contact',
      accessor: 'email',
      render: (row) => (
        <div>
          <div className="flex items-center text-sm text-gray-900 mb-1">
            <Mail className="w-4 h-4 mr-2 text-gray-400" />
            {renderCell(row.email)}
          </div>
          <div className="flex items-center text-sm text-gray-500">
            <Phone className="w-4 h-4 mr-2 text-gray-400" />
            {renderCell(row.phone)}
          </div>
        </div>
      ),
    },
    {
      header: 'Adresse',
      accessor: 'address',
      render: (row) => (
        <span className="text-sm text-gray-700">{renderCell(row.address)}</span>
      ),
    },
    {
      header: 'Date création',
      accessor: 'createdAt',
      render: (row) => (
        <span className="text-sm text-gray-700">{renderCellDate(row.createdAt)}</span>
      ),
    },
    {
      header: 'Actions',
      accessor: 'actions',
      render: (row) => (
        <RowActions
          onView={() => navigate(`/clients/${row.id}/sites`)}
          onEdit={() => navigate(`/clients/edit/${row.id}`)}
          onDelete={() => setDeleteModal({ open: true, client: row })}
        >
          <button
            type="button"
            onClick={() => openPasswordModal(row)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Mot de passe"
          >
            <Lock className="w-4 h-4" />
          </button>
          <button
            onClick={() => navigate(`/clients/${row.id}/sites`)}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Sites"
          >
            <MapPin className="w-4 h-4" />
          </button>
          {row.status === 'active' ? (
            <button
              onClick={() => handleDeactivate(row.id)}
              className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
              title="Désactiver"
            >
              <Ban className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={() => handleActivate(row.id)}
              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
              title="Activer"
            >
              <CheckCircle className="w-4 h-4" />
            </button>
          )}
        </RowActions>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // Calculate stats
  const totalClients = clients.length;
  const activeClients = clients.filter(c => c.status === 'active' || !c.status).length;
  const inactiveClients = clients.filter(c => c.status === 'inactive').length;
  const newClientsThisMonth = clients.filter(c => {
    const createdDate = new Date(c.createdAt);
    const now = new Date();
    return createdDate.getMonth() === now.getMonth() && createdDate.getFullYear() === now.getFullYear();
  }).length;

  return (
    <div className="space-y-3 sm:space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Gestion des Clients</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Gérez vos clients et leurs informations</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'table' ? 'primary' : 'outline'}
            size="sm"
            icon={List}
            onClick={() => setViewMode('table')}
          >
            Tableau
          </Button>
          <Button
            variant={viewMode === 'card' ? 'primary' : 'outline'}
            size="sm"
            icon={Grid}
            onClick={() => setViewMode('card')}
          >
            Cartes
          </Button>
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => navigate('/clients/add')}
            className="w-full sm:w-auto"
          >
            Ajouter un client
          </Button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0 }}
          whileHover={{ y: -3, scale: 1.02 }}
          className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-100 p-3 sm:p-4 hover:shadow-2xl hover:border-blue-200 transition-all"
        >
          <div>
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className="p-2 sm:p-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg sm:rounded-xl shadow-lg shadow-blue-500/30">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
            </div>
            <p className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Total Clients</p>
            <p className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-none">{totalClients}</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          whileHover={{ y: -3, scale: 1.02 }}
          className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-100 p-3 sm:p-4 hover:shadow-2xl hover:border-green-200 transition-all"
        >
          <div>
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className="p-2 sm:p-2.5 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg sm:rounded-xl shadow-lg shadow-green-500/30">
                <UserCheck className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <span className="text-xs sm:text-sm font-bold text-green-600 bg-green-50 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg">
                {totalClients > 0 ? Math.round((activeClients / totalClients) * 100) : 0}%
              </span>
            </div>
            <p className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Actifs</p>
            <p className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-none">{activeClients}</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          whileHover={{ y: -3, scale: 1.02 }}
          className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-100 p-3 sm:p-4 hover:shadow-2xl hover:border-red-200 transition-all"
        >
          <div>
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className="p-2 sm:p-2.5 bg-gradient-to-br from-red-500 to-rose-600 rounded-lg sm:rounded-xl shadow-lg shadow-red-500/30">
                <UserX className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <span className="text-xs sm:text-sm font-bold text-red-600 bg-red-50 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg">
                {totalClients > 0 ? Math.round((inactiveClients / totalClients) * 100) : 0}%
              </span>
            </div>
            <p className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Inactifs</p>
            <p className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-none">{inactiveClients}</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          whileHover={{ y: -3, scale: 1.02 }}
          className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-100 p-3 sm:p-4 hover:shadow-2xl hover:border-purple-200 transition-all"
        >
          <div>
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className="p-2 sm:p-2.5 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg sm:rounded-xl shadow-lg shadow-purple-500/30">
                <UserPlus className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <span className="text-xs sm:text-sm font-bold text-purple-600 bg-purple-50 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg">
                Ce mois
              </span>
            </div>
            <p className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Nouveaux</p>
            <p className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-none">{newClientsThisMonth}</p>
          </div>
        </motion.div>
      </div>

      {/* Search and Filters */}
      <Card>
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <Input
              placeholder="Rechercher un client..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon={Search}
            />
          </div>
          <Button variant="outline">Filtres</Button>
        </div>
      </Card>

      {/* Clients Table View */}
      {viewMode === 'table' && (
        <Card>
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {filteredClients.length} client(s)
            </h2>
          </div>
          <Table columns={columns} data={filteredClients} />
        </Card>
      )}

      {/* Clients Card View */}
      {viewMode === 'card' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredClients.map((client, index) => (
            <motion.div
              key={client.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className="hover:shadow-lg transition-shadow">
                <div className="space-y-4">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-3 rounded-lg ${client.status === 'active' || !client.status ? 'bg-green-100' : 'bg-red-100'}`}>
                        <Users className={`w-5 h-5 ${client.status === 'active' || !client.status ? 'text-green-600' : 'text-red-600'}`} />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{client.name}</h3>
                        <p className="text-sm text-gray-500">{client.company}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${client.status === 'active' || !client.status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {client.status === 'active' || !client.status ? 'Actif' : 'Inactif'}
                    </span>
                  </div>

                  {/* Contact Info */}
                  <div className="space-y-2">
                    <div className="flex items-center text-sm text-gray-700">
                      <Mail className="w-4 h-4 mr-2 text-gray-400" />
                      {client.email}
                    </div>
                    <div className="flex items-center text-sm text-gray-700">
                      <Phone className="w-4 h-4 mr-2 text-gray-400" />
                      {client.phone}
                    </div>
                    {client.address && (
                      <div className="flex items-center text-sm text-gray-700">
                        <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                        {client.address}
                      </div>
                    )}
                  </div>

                  {/* Date */}
                  <div className="text-xs text-gray-500">
                    Créé le {formatDate(client.createdAt)}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end pt-2 border-t border-gray-200">
                    <RowActions
                      onView={() => navigate(`/clients/${client.id}/sites`)}
                      onEdit={() => navigate(`/clients/edit/${client.id}`)}
                      onDelete={() => setDeleteModal({ open: true, client })}
                    >
                      <button
                        type="button"
                        onClick={() => openPasswordModal(client)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Mot de passe"
                      >
                        <Lock className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate(`/clients/${client.id}/sites`)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Sites"
                      >
                        <MapPin className="w-4 h-4" />
                      </button>
                      {client.status === 'active' || !client.status ? (
                        <button
                          type="button"
                          onClick={() => handleDeactivate(client.id)}
                          className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                          title="Désactiver"
                        >
                          <Ban className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleActivate(client.id)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Activer"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      )}
                    </RowActions>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {filteredClients.length === 0 && (
        <Card>
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Aucun client trouvé
            </h3>
            <p className="text-gray-600">
              {searchQuery ? 'Essayez un autre terme de recherche' : 'Commencez par ajouter un client'}
            </p>
          </div>
        </Card>
      )}

      {/* Password Modal */}
      <Modal
        isOpen={passwordModal.open}
        onClose={() =>
          setPasswordModal({
            open: false,
            client: null,
            currentPassword: '',
            newPassword: '',
            saving: false,
            copied: false,
          })
        }
        title={`Mot de passe — ${passwordModal.client?.name || ''}`}
      >
        <div className="space-y-4">
          {passwordModal.currentPassword ? (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm font-medium text-blue-800 mb-2">Mot de passe actuel (base de données)</p>
              <p className="font-mono text-lg font-bold text-blue-900 select-all">
                {passwordModal.currentPassword}
              </p>
            </div>
          ) : (
            <p className="text-sm text-yellow-800 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              Aucun mot de passe enregistré pour ce client.
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              icon={RefreshCw}
              onClick={() =>
                setPasswordModal((prev) => ({
                  ...prev,
                  newPassword: generatePassword(),
                  copied: false,
                }))
              }
            >
              Générer un mot de passe
            </Button>
            {passwordModal.newPassword && (
              <Button
                type="button"
                variant="outline"
                icon={passwordModal.copied ? Check : Copy}
                onClick={() => {
                  navigator.clipboard.writeText(passwordModal.newPassword);
                  setPasswordModal((prev) => ({ ...prev, copied: true }));
                }}
              >
                {passwordModal.copied ? 'Copié' : 'Copier'}
              </Button>
            )}
          </div>

          <Input
            label="Nouveau mot de passe"
            name="newPassword"
            value={passwordModal.newPassword}
            onChange={(e) =>
              setPasswordModal((prev) => ({
                ...prev,
                newPassword: e.target.value,
                copied: false,
              }))
            }
            placeholder="Générer ou saisir manuellement"
          />

          <div className="flex items-center justify-end space-x-3">
            <Button
              variant="secondary"
              onClick={() =>
                setPasswordModal({
                  open: false,
                  client: null,
                  currentPassword: '',
                  newPassword: '',
                  saving: false,
                  copied: false,
                })
              }
            >
              Fermer
            </Button>
            <Button
              variant="primary"
              icon={Lock}
              loading={passwordModal.saving}
              onClick={handlePasswordReset}
            >
              Enregistrer dans la base
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, client: null })}
        title="Supprimer le client"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Êtes-vous sûr de vouloir supprimer le client{' '}
            <strong>{deleteModal.client?.name}</strong> ? Cette action est irréversible.
          </p>
          <div className="flex items-center justify-end space-x-3">
            <Button
              variant="secondary"
              onClick={() => setDeleteModal({ open: false, client: null })}
            >
              Annuler
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              Supprimer
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ClientsManagement;

